import {
	afterNextRender,
	Component,
	DestroyRef,
	DOCUMENT,
	ElementRef,
	inject,
	Injector,
	viewChild
} from '@angular/core';
import { UiButtonComponent, UiEmptyStateComponent } from '@design-system';
import { SessionService } from '../../../../services/session.service';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';

/**
 * Confirmation in front of خروج.
 *
 * Tapping خروج must not end the session on its own, so the button only raises
 * this sheet; the session is ended from here, and only by تأیید. Backing out —
 * انصراف, backdrop, swipe-down, Escape or back — leaves the user signed in and
 * on the سایر page, which is what every route out of the sheet but تأیید does.
 *
 * The end itself is `SessionService.signOut`, not `AuthService.logout`: the kala
 * session has to be released on the backend (`POST /users/logout`) before the app
 * hands over to the account server's sign-out, and only the former does that.
 */
@Component({
	selector: 'app-logout-bottom-sheet',
	imports: [BottomSheetComponent, UiButtonComponent, UiEmptyStateComponent],
	templateUrl: './logout-bottom-sheet.component.html',
	host: {
		'data-cy': 'logout-bottom-sheet'
	}
})
export class LogoutBottomSheetComponent {
	private readonly session = inject(SessionService);
	private readonly injector = inject(Injector);
	private readonly document = inject(DOCUMENT);

	/** Keeps the sheet from taking a second tap while the release is in flight. */
	readonly signingOut = this.session.signingOut;

	/** `open` resumes after an await, by which point the host may be gone. */
	private isDestroyed = false;

	constructor() {
		inject(DestroyRef).onDestroy(() => (this.isDestroyed = true));
	}

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');
	private readonly confirmButton = viewChild('confirmButton', { read: ElementRef });

	/** Whatever raised the sheet, so a close hands focus back where it came from. */
	private opener: HTMLElement | null = null;

	async open(): Promise<void> {
		this.opener = this.activeElement();
		await (this.sheet()?.open() ?? Promise.resolve());
		if (this.isDestroyed) {
			return;
		}

		// Move focus onto the confirm action so keyboard and screen reader users land on
		// the newly raised sheet instead of losing focus to <body>.
		afterNextRender(() => this.confirmButton()?.nativeElement.querySelector('button')?.focus(), {
			injector: this.injector
		});
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/**
	 * Ends the session. The kala release runs first, then the redirect to the
	 * identity provider's sign-out, so the sheet is left as it is — there is no
	 * page underneath to hand focus back to.
	 */
	onConfirm(): void {
		this.session.signOut();
	}

	onDismiss(): Promise<void> {
		return this.close();
	}

	onSheetClosed(): void {
		const opener = this.opener;
		this.opener = null;
		if (opener?.isConnected) {
			opener.focus();
		}
	}

	private activeElement(): HTMLElement | null {
		const active = this.document.activeElement;
		return active instanceof HTMLElement && active !== this.document.body ? active : null;
	}
}
